import { ComputerOutlined, LocationOnOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { AusschreibungStatusTag } from '../../components/general/AusschreibungStatusTag.tsx';
import { VeranstaltungHealthIcon } from '../../components/general/HealthIcon/VeranstaltungHealthIcon.tsx';
import { Tag } from '../../components/general/Tag.tsx';
import { LeistungIndexTags } from '../../components/general/ThemaHeader/LeistungIndexTags.tsx';
import {
  capabilities as capabilitiesEnum,
  sapStatus as sapStatusDictionary,
  ThemaFromThemaRouter,
} from '../../dtos.ts';
import { elevateColorPalette } from '../../theme/colorPalette.ts';
import { formatTimeRangeForDate, printWithNoInfo } from '../../utils';
import { determineStatusText } from '../../utils/determineStatusText.ts';
import { getMultiExperteName } from '../../utils/getBezeichnungen/getBezeichnungen.ts';
import { getLeistungstemplatesFromModul } from '../../utils/getLeistungstemplatesFromModul.ts';
import { printModulFormat } from '../../utils/printModulFormat.ts';
import { getAllLeistungen } from '../../utils/teilleistung.ts';
import { UpdateRessourcenBlockungen } from '../VeranstaltungDetailsPage/UpdateRessourcenBlockungen.tsx';
import { Modultermin } from './DurchgangDetailsPage.types.ts';

type ModulterminCardProps = {
  readonly modultermin: Modultermin;
  thema: ThemaFromThemaRouter;
};

export const ModulterminCard = ({
  modultermin: {
    id,
    akaVeranstaltungId,
    akaDurchgangId,
    modul,
    ablauf,
    experteBlockungen,
    raumBlockungen,
    vivaStatus,
    sapStatus,
    teilleistungen,
    capabilities,
    manuellerAusschreibungsprozess,
    akaModulId,
    onlineTool,
    standortBlockungen,
    experteTermine,
  },
  thema,
}: ModulterminCardProps) => {
  const isAbgesagt = sapStatus === sapStatusDictionary.ABGESAGT;
  const textColor = isAbgesagt ? elevateColorPalette.text.disabledSolid : elevateColorPalette.text.primary;

  const ausschreibungStatuses = getAllLeistungen(teilleistungen).flatMap((leistung) => {
    const status = leistung.ausschreibung?.status;
    return status ? [{ leistungId: leistung.leistungId, status }] : [];
  });
  const themaModul = thema.module.find((m) => m.modulnummer === modul.modulnummer);
  const leistungstemplates = themaModul ? getLeistungstemplatesFromModul(themaModul) : [];

  const canEditModultermin = (): boolean =>
    capabilities.includes(capabilitiesEnum.EDIT_EXPERTE) || capabilities.includes(capabilitiesEnum.EDIT_LOCATION);

  const getEditType = () => {
    if (capabilities.includes(capabilitiesEnum.EDIT_EXPERTE) && capabilities.includes(capabilitiesEnum.EDIT_LOCATION)) {
      return 'all';
    }
    if (capabilities.includes(capabilitiesEnum.EDIT_LOCATION)) {
      return 'raum';
    }
    return 'experte';
  };

  return (
    <Box
      sx={{
        p: 2,
        background: (theme) => theme.palette.surface.background,
        border: '1px solid',
        borderColor: (theme) => (isAbgesagt ? theme.palette.border.disabled : theme.palette.border.default),
        borderRadius: '4px',
      }}
      key={modul.modulnummer}
    >
      <Box display="grid" gap={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            variant="paragraph"
            fontWeight={500}
            display="inline-flex"
            alignItems="center"
            gap={1}
            color={textColor}
          >
            {isAbgesagt ? (
              <span>
                <s>{modul.titel}</s>
                &nbsp;(abgesagt)
              </span>
            ) : (
              <>
                <VeranstaltungHealthIcon akaVeranstaltungId={akaVeranstaltungId} /> {modul.titel}
              </>
            )}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" justifyContent="flex-end">
            <Tag style="neutral" size="small">
              {determineStatusText(vivaStatus)}
            </Tag>
            {ausschreibungStatuses.map(({ leistungId, status }) => (
              <AusschreibungStatusTag key={leistungId} status={status} />
            ))}
          </Box>
        </Box>

        <div className="flex gap-4">
          <Tag style={isAbgesagt ? 'neutral' : 'brand'} size="small">
            {modul.isOnline ? (
              <ComputerOutlined sx={{ fontSize: '16px' }} />
            ) : (
              <LocationOnOutlined sx={{ fontSize: '16px' }} />
            )}
            {printModulFormat(modul.modulFormat)}
          </Tag>
          <LeistungIndexTags leistungstemplates={leistungstemplates} />
        </div>

        <Box display="flex" alignItems="center">
          <Box display="grid" gap={4} gridTemplateColumns="repeat(3, 1fr)" flexGrow={1}>
            <Box display="flex" gap={1}>
              <Typography variant="labelMultilineLight" color="textTertiary">
                Datum
              </Typography>
              <Box>
                {ablauf.map((tag, i) => (
                  <Typography
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={`moduleDate-${modul.modulnummer}-${i}-${tag.start.toISOString()}`}
                    variant="labelMultilineLight"
                    component="p"
                    color={textColor}
                  >
                    {formatTimeRangeForDate(tag.start, tag.end)}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Box display="flex" gap={1}>
              <Typography variant="labelMultilineLight" color="textTertiary">
                Trainer:in
              </Typography>
              <Box>
                {ablauf.flatMap((tag) => (
                  <Typography
                    variant="labelMultilineLight"
                    component="p"
                    key={`experteTag${tag.start}`}
                    color={textColor}
                  >
                    {printWithNoInfo(getMultiExperteName(experteBlockungen.map((blockung) => blockung.experte)))}
                  </Typography>
                ))}
              </Box>
            </Box>

            {!modul.isOnline && (
              <Box display="flex" gap={1}>
                <Typography variant="labelMultilineLight" color="textTertiary">
                  Seminarraum
                </Typography>
                <Box>
                  {ablauf.flatMap((tag) => (
                    <Typography
                      variant="labelMultilineLight"
                      component="p"
                      key={`seminarraumTag${tag.start}`}
                      color={textColor}
                    >
                      {getSeminarraumKuerzel(raumBlockungen.map((blockung) => blockung.raum)) ?? '-'}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {canEditModultermin() && (
            <UpdateRessourcenBlockungen
              veranstaltung={{
                id,
                akaVeranstaltungId,
                akaDurchgangId,
                experteBlockungen,
                raumBlockungen,
                manuellerAusschreibungsprozess,
                akaModulId,
                ablauf,
                vivaStatus,
                onlineTool,
                teilleistungen,
                standortBlockungen,
                experteTermine,
                capabilities,
              }}
              thema={thema}
              editType={getEditType()}
              isIconButton={true}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

function getSeminarraumKuerzel(raeume: { isGruppenraum: boolean; kuerzel: string }[]): string | undefined {
  return raeume.find((raum) => !raum.isGruppenraum)?.kuerzel;
}
